import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedCharacter } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface CharacterStore {
  characters: SavedCharacter[]
  addCharacter: (character: SavedCharacter) => void
  updateCharacter: (id: string, character: Partial<SavedCharacter>) => void
  deleteCharacter: (id: string) => void
  clearCharacters: () => void
  getCharacterById: (id: string) => SavedCharacter | undefined
  exportAsText: () => void
  exportAsJSON: () => void
  exportSingleAsText: (id: string) => void
  exportSingleAsJSON: (id: string) => void
  importFromJSON: (jsonString: string) => void
  toggleFavorite: (id: string) => void
  isFavorite: (id: string) => boolean
  getFavoriteCharacters: () => SavedCharacter[]
  addTag: (id: string, tag: string) => void
  removeTag: (id: string, tag: string) => void
  updateMemo: (id: string, memo: string) => void
}

export const useCharacterStore = create<CharacterStore>()(
  persist(
    (set, get) => ({
      characters: [],

      addCharacter: (character) => {
        set((state) => {
          // inputから不要なコンテキストデータを除外（生成時の材料データは保存しない）
          const cleanedInput = { ...character.input }
          delete cleanedInput.uploadedContent      // 参考テキスト
          delete cleanedInput.images               // 参考画像（Base64）
          delete cleanedInput.relatedCharactersContext  // 関連キャラクターの全データ
          delete cleanedInput.communityContext     // コミュニティの全データ
          delete cleanedInput.societyContext       // ソサエティの全データ
          delete cleanedInput.itemsContext         // アイテムの全データ
          delete cleanedInput.skillsContext        // スキルの全データ
          delete cleanedInput.worldContext         // 世界観テキスト

          const cleanedCharacter = {
            ...character,
            input: cleanedInput
          }

          const updatedCharacters = [cleanedCharacter, ...state.characters]

          if (updatedCharacters.length > 500) {
            updatedCharacters.splice(500)
          }

          return { characters: updatedCharacters }
        })
      },

      updateCharacter: (id, updatedCharacter) => {
        set((state) => ({
          characters: state.characters.map((character) =>
            character.id === id 
              ? { ...character, ...updatedCharacter, updatedAt: new Date().toISOString() }
              : character
          )
        }))
      },

      deleteCharacter: (id) => {
        set((state) => ({
          characters: state.characters.filter((character) => character.id !== id)
        }))
      },

      clearCharacters: () => {
        set({ characters: [] })
      },

      getCharacterById: (id) => {
        return get().characters.find((character) => character.id === id)
      },

      exportAsText: () => {
        const { characters } = get()
        if (characters.length === 0) {
          alert('保存されたキャラクターがありません。')
          return
        }

        const content = formatCharactersForExport(characters)
        downloadTextFile(content, `saved_characters_${Date.now()}.txt`)
      },

      exportAsJSON: () => {
        const { characters } = get()
        if (characters.length === 0) {
          alert('保存されたキャラクターがありません。')
          return
        }

        const cleanedCharacters = characters.map(cleanCharacterForExport)

        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'characters',
          data: cleanedCharacters
        }

        downloadJSONFile(exportData, `saved_characters_${Date.now()}.json`)
      },

      exportSingleAsText: (id: string) => {
        const character = get().getCharacterById(id)
        if (!character) {
          alert('キャラクターが見つかりません。')
          return
        }

        const content = formatCharactersForExport([character])
        downloadTextFile(content, `character_${character.result.name}_${Date.now()}.txt`)
      },

      exportSingleAsJSON: (id: string) => {
        const character = get().getCharacterById(id)
        if (!character) {
          alert('キャラクターが見つかりません。')
          return
        }

        const cleanedCharacter = cleanCharacterForExport(character)

        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'character',
          data: cleanedCharacter
        }

        downloadJSONFile(exportData, `character_${character.result.name}_${Date.now()}.json`)
      },

      importFromJSON: (jsonString: string) => {
        try {
          const importData = JSON.parse(jsonString)
          
          // バリデーション
          if (!importData.data && !importData.characters) {
            throw new Error('無効なデータ形式です')
          }

          const currentCharacters = get().characters
          // 単独データか複数データかを判定
          let newCharacters: SavedCharacter[]
          if (importData.dataType === 'character' && importData.data && !Array.isArray(importData.data)) {
            // 単独キャラクターの場合
            newCharacters = [importData.data as SavedCharacter]
          } else {
            // 複数キャラクターの場合
            newCharacters = (importData.data || importData.characters) as SavedCharacter[]
          }
          
          // 重複チェック（タイトルとキャラクター名で判定）
          const uniqueCharacters = newCharacters.filter(newChar => {
            return !currentCharacters.some(existing => 
              existing.title === newChar.title && 
              existing.result.name === newChar.result.name
            )
          })
          
          // 最大保存数チェック
          const maxCharacters = 500
          const totalCount = currentCharacters.length + uniqueCharacters.length
          if (totalCount > maxCharacters) {
            const availableSlots = maxCharacters - currentCharacters.length
            if (availableSlots <= 0) {
              alert(`保存数が上限（${maxCharacters}件）に達しています。不要なキャラクターを削除してください。`)
              return
            }
            if (!confirm(`追加可能な件数は${availableSlots}件です。${uniqueCharacters.length}件中${availableSlots}件のみインポートしますか？`)) {
              return
            }
            uniqueCharacters.splice(availableSlots)
          }

          // IDの重複を避けるため、新しいIDを生成して追加
          const importedCharacters = uniqueCharacters.map(character => ({
            ...character,
            id: crypto.randomUUID(),
            createdAt: character.createdAt || new Date().toISOString(),
            updatedAt: character.updatedAt || new Date().toISOString()
          }))
          
          const mergedCharacters = [...currentCharacters, ...importedCharacters]

          set({ characters: mergedCharacters })
          
          const skippedCount = newCharacters.length - uniqueCharacters.length
          alert(`${importedCharacters.length}件のキャラクターをインポートしました。${skippedCount > 0 ? `(${skippedCount}件の重複をスキップ)` : ''}`)
        } catch (error) {
          console.error('Import error:', error)
          alert('インポートに失敗しました。ファイル形式を確認してください。')
        }
      },

      toggleFavorite: (id) => {
        set((state) => ({
          characters: state.characters.map((character) =>
            character.id === id
              ? {
                  ...character,
                  favorite: !character.favorite,
                  updatedAt: new Date().toISOString()
                }
              : character
          )
        }))
      },

      isFavorite: (id) => {
        const character = get().characters.find(c => c.id === id)
        return character?.favorite || false
      },

      getFavoriteCharacters: () => {
        return get().characters.filter(c => c.favorite)
      },

      addTag: (id, tag) => {
        set((state) => ({
          characters: state.characters.map((character) =>
            character.id === id
              ? {
                  ...character,
                  tags: [...(character.tags || []), tag],
                  updatedAt: new Date().toISOString()
                }
              : character
          )
        }))
      },

      removeTag: (id, tag) => {
        set((state) => ({
          characters: state.characters.map((character) =>
            character.id === id
              ? {
                  ...character,
                  tags: (character.tags || []).filter(t => t !== tag),
                  updatedAt: new Date().toISOString()
                }
              : character
          )
        }))
      },

      updateMemo: (id, memo) => {
        set((state) => ({
          characters: state.characters.map((character) =>
            character.id === id
              ? {
                  ...character,
                  memo,
                  updatedAt: new Date().toISOString()
                }
              : character
          )
        }))
      }
    }),
    {
      name: 'character-store',
      version: 2,
      storage: createIndexedDBStorage('character-store'),
      partialize: (state) => ({ characters: state.characters }),
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          return {
            ...persistedState,
            characters: persistedState.characters || []
          }
        }
        // version 1 → 2: inputから不要なコンテキストデータを削除
        if (version === 1) {
          const cleanedCharacters = (persistedState.characters || []).map((character: any) => {
            if (!character.input) return character
            const cleanedInput = { ...character.input }
            delete cleanedInput.uploadedContent
            delete cleanedInput.images
            delete cleanedInput.relatedCharactersContext
            delete cleanedInput.communityContext
            delete cleanedInput.societyContext
            delete cleanedInput.itemsContext
            delete cleanedInput.skillsContext
            delete cleanedInput.worldContext
            return { ...character, input: cleanedInput }
          })
          return { ...persistedState, characters: cleanedCharacters }
        }
        return persistedState as CharacterStore
      }
    }
  )
)

// キャラクターをテキスト形式にフォーマット
function formatCharactersForExport(characters: SavedCharacter[]): string {
  const date = new Date().toLocaleString('ja-JP')

  let content = `【保存されたキャラクター一覧】
生成日時: ${date}
総数: ${characters.length}件

`

  characters.forEach((character, index) => {
    const { result } = character
    content += `\n【${index + 1}】 ${result.name}\n`
    content += '='.repeat(50) + '\n\n'

    // 基本情報
    content += `【基本情報】\n`
    content += `名前: ${result.name || 'データなし'}\n`
    content += `年齢: ${result.age || 'データなし'}\n`
    content += `性別: ${result.gender || 'データなし'}\n`
    content += `役割: ${result.role || 'データなし'}\n\n`

    // 概要
    content += `【概要】\n${result.summary || 'データなし'}\n\n`

    // 性格
    content += `【性格】\n`
    content += `特性: ${result.personality?.traits?.join(', ') || 'データなし'}\n`
    content += `長所: ${result.personality?.strengths?.join(', ') || 'データなし'}\n`
    content += `短所: ${result.personality?.weaknesses?.join(', ') || 'データなし'}\n`
    content += `恐れ: ${result.personality?.fears?.join(', ') || 'データなし'}\n`
    content += `欲求: ${result.personality?.desires?.join(', ') || 'データなし'}\n`
    content += `価値観: ${result.personality?.values?.join(', ') || 'データなし'}\n`
    if (result.personality?.likes?.length) {
      content += `好きなもの: ${result.personality.likes.join(', ')}\n`
    }
    if (result.personality?.dislikes?.length) {
      content += `嫌いなもの: ${result.personality.dislikes.join(', ')}\n`
    }
    if (result.personality?.goals?.length) {
      content += `目標: ${result.personality.goals.join(', ')}\n`
    }
    content += '\n'

    // 背景
    content += `【背景】\n`
    if (result.background?.species) {
      content += `種族・民族: ${result.background.species}\n`
    }
    if (result.background?.language) {
      content += `言語・方言: ${result.background.language}\n`
    }
    content += `出身地: ${result.background?.birthplace || 'データなし'}\n`
    content += `家族: ${result.background?.family || 'データなし'}\n`
    content += `教育: ${result.background?.education || 'データなし'}\n`
    content += `職業: ${result.background?.occupation || 'データなし'}\n`
    content += `重要な出来事:\n`
    content += result.background?.significantEvents?.map(e => `  - ${e}`).join('\n') || '  - データなし'
    content += '\n'
    content += `人間関係:\n`
    content += result.background?.relationships?.map(r => {
      if (typeof r === 'string') return `  - ${r}`
      if (typeof r === 'object' && r !== null) {
        const name = (r as any).name || (r as any).character || ''
        const relation = (r as any).relation || (r as any).relationship || ''
        return `  - ${name} - ${relation}`
      }
      return `  - ${String(r)}`
    }).join('\n') || '  - データなし'
    content += '\n'
    if (result.background?.secrets && result.background.secrets.length > 0) {
      content += `秘密:\n`
      content += result.background.secrets.map(s => `  - ${s}`).join('\n')
      content += '\n'
    }
    content += `能力・スキル:\n`
    content += result.background?.abilities?.map(a => `  - ${a}`).join('\n') || '  - データなし'
    content += '\n'
    if (result.background?.backstoryNarrative) {
      content += `バックストーリー: ${result.background.backstoryNarrative}\n`
    }
    content += '\n'

    // 外見
    content += `【外見】\n`
    content += `身長: ${result.appearance?.height || 'データなし'}\n`
    if (result.appearance?.weight) {
      content += `体重: ${result.appearance.weight}\n`
    }
    content += `体格: ${result.appearance?.build || 'データなし'}\n`
    content += `髪: ${result.appearance?.hairColor || 'データなし'} / ${result.appearance?.hairStyle || 'データなし'}\n`
    content += `瞳: ${result.appearance?.eyeColor || 'データなし'}\n`
    content += `顔立ち: ${result.appearance?.facialFeatures || 'データなし'}\n`
    content += `服装: ${result.appearance?.clothing || 'データなし'}\n`
    if (result.appearance?.distinctiveMarks) {
      content += `特徴: ${result.appearance.distinctiveMarks}\n`
    }
    content += `印象: ${result.appearance?.overallImpression || 'データなし'}\n`
    if (result.appearance?.voiceQuality) {
      content += `声質: ${result.appearance.voiceQuality}\n`
    }
    if (result.appearance?.posture) {
      content += `姿勢: ${result.appearance.posture}\n`
    }
    content += '\n'

    // 行動パターン
    content += `【行動パターン】\n`
    content += `一人称: ${result.behavior?.firstPerson || '私'}\n`
    content += `話し方: ${result.behavior?.speechPattern || 'データなし'}\n`
    if (result.behavior?.catchphrases?.length) {
      content += `口癖: ${result.behavior.catchphrases.join(', ')}\n`
    }
    content += `癖: ${result.behavior?.mannerisms?.join(', ') || 'データなし'}\n`
    content += `習慣: ${result.behavior?.habits?.join(', ') || 'データなし'}\n`
    content += `対人スタイル: ${result.behavior?.socialStyle || 'データなし'}\n`
    content += `意思決定: ${result.behavior?.decisionMaking || 'データなし'}\n`
    content += `ストレス反応: ${result.behavior?.stressResponse || 'データなし'}\n`
    content += `対立への対処: ${result.behavior?.conflictResolution || 'データなし'}\n`
    content += `武器・戦闘スタイル:\n`
    content += result.behavior?.combatStyle?.map(c => `  - ${c}`).join('\n') || '  - データなし'
    content += '\n\n'

    // 成長・発展
    content += `【成長・発展】\n`
    content += `現在の状態: ${result.development?.currentState || 'データなし'}\n`
    content += `成長の可能性:\n`
    content += result.development?.growthPotential?.map(g => `  - ${g}`).join('\n') || '  - データなし'
    content += '\n'
    content += `内的葛藤:\n`
    content += result.development?.internalConflicts?.map(c => `  - ${c}`).join('\n') || '  - データなし'
    content += '\n'
    content += `外的課題:\n`
    content += result.development?.externalChallenges?.map(c => `  - ${c}`).join('\n') || '  - データなし'
    content += '\n'
    content += `展開案:\n`
    content += result.development?.arcSuggestions?.map(a => `  - ${a}`).join('\n') || '  - データなし'
    content += '\n\n'

    // 感情表現
    if (result.emotionalExpression) {
      content += `【感情表現】\n`
      if (result.emotionalExpression.joy) content += `喜び: ${result.emotionalExpression.joy}\n`
      if (result.emotionalExpression.anger) content += `怒り: ${result.emotionalExpression.anger}\n`
      if (result.emotionalExpression.sadness) content += `哀しみ: ${result.emotionalExpression.sadness}\n`
      if (result.emotionalExpression.enjoyment) content += `楽しみ: ${result.emotionalExpression.enjoyment}\n`
      if (result.emotionalExpression.emotionalControl) content += `感情抑制: ${result.emotionalExpression.emotionalControl}\n`
      if (result.emotionalExpression.stressRelief) content += `ストレス発散: ${result.emotionalExpression.stressRelief}\n`
      if (result.emotionalExpression.cryingStyle) content += `泣き方: ${result.emotionalExpression.cryingStyle}\n`
      if (result.emotionalExpression.laughingStyle) content += `笑い方: ${result.emotionalExpression.laughingStyle}\n`
      content += '\n'
    }

    // 対人関係パターン
    if (result.interpersonalPatterns) {
      content += `【対人関係パターン】\n`
      if (result.interpersonalPatterns.firstMeetingBehavior) content += `初対面: ${result.interpersonalPatterns.firstMeetingBehavior}\n`
      if (result.interpersonalPatterns.trustCriteria) content += `信頼基準: ${result.interpersonalPatterns.trustCriteria}\n`
      if (result.interpersonalPatterns.distanceCriteria) content += `距離を置く基準: ${result.interpersonalPatterns.distanceCriteria}\n`
      if (result.interpersonalPatterns.romanticView) content += `恋愛観: ${result.interpersonalPatterns.romanticView}\n`
      if (result.interpersonalPatterns.hierarchyBehavior) content += `上下関係: ${result.interpersonalPatterns.hierarchyBehavior}\n`
      if (result.interpersonalPatterns.groupPosition) content += `集団での立場: ${result.interpersonalPatterns.groupPosition}\n`
      content += '\n'
    }

    // 思考プロセス
    if (result.thinkingProcess) {
      content += `【思考プロセス】\n`
      if (result.thinkingProcess.problemSolvingApproach) content += `問題解決: ${result.thinkingProcess.problemSolvingApproach}\n`
      if (result.thinkingProcess.crisisJudgment) content += `危機判断: ${result.thinkingProcess.crisisJudgment}\n`
      if (result.thinkingProcess.prioritization) content += `優先順位: ${result.thinkingProcess.prioritization}\n`
      if (result.thinkingProcess.planningAbility) content += `計画性: ${result.thinkingProcess.planningAbility}\n`
      if (result.thinkingProcess.intuitionLogicBalance) content += `直感/論理: ${result.thinkingProcess.intuitionLogicBalance}\n`
      content += '\n'
    }

    // 日常生活
    if (result.dailyLife) {
      content += `【日常生活】\n`
      if (result.dailyLife.routine) content += `日課: ${result.dailyLife.routine}\n`
      if (result.dailyLife.importantItems?.length) content += `大切なもの: ${result.dailyLife.importantItems.join(', ')}\n`
      content += '\n'
    }

    // 信念
    if (result.beliefs) {
      content += `【信念・哲学】\n`
      if (result.beliefs.lifePhilosophy) content += `人生哲学: ${result.beliefs.lifePhilosophy}\n`
      if (result.beliefs.workBeliefs) content += `仕事への信念: ${result.beliefs.workBeliefs}\n`
      if (result.beliefs.worldview) content += `世界観: ${result.beliefs.worldview}\n`
      if (result.beliefs.coreValues) content += `価値観: ${result.beliefs.coreValues}\n`
      if (result.beliefs.faith) content += `信仰: ${result.beliefs.faith}\n`
      content += '\n'
    }

    // 専門性
    if (result.expertise) {
      content += `【専門性】\n`
      if (result.expertise.mainField) content += `専門分野: ${result.expertise.mainField}\n`
      if (result.expertise.practicalSkills?.length) content += `実務能力: ${result.expertise.practicalSkills.join(', ')}\n`
      if (result.expertise.intellectualWorks) content += `知的活動: ${result.expertise.intellectualWorks}\n`
      if (result.expertise.characteristicEpisodes?.length) {
        content += `エピソード:\n`
        result.expertise.characteristicEpisodes.forEach(e => content += `  - ${e}\n`)
      }
      content += '\n'
    }

    // 関係性詳細
    if (result.relationshipDetails) {
      content += `【関係性詳細】\n`
      if (result.relationshipDetails.nicknames && Object.keys(result.relationshipDetails.nicknames).length > 0) {
        content += `呼び方:\n`
        Object.entries(result.relationshipDetails.nicknames).forEach(([name, nickname]) => {
          content += `  - ${name}: ${nickname}\n`
        })
      }
      if (result.relationshipDetails.respectedPeople?.length) content += `尊敬する人: ${result.relationshipDetails.respectedPeople.join(', ')}\n`
      if (result.relationshipDetails.rivals?.length) content += `ライバル: ${result.relationshipDetails.rivals.join(', ')}\n`
      content += '\n'
    }

    // サンプル台詞
    content += `【サンプル台詞】\n`
    if (result.sampleDialogues && result.sampleDialogues.length > 0) {
      result.sampleDialogues.forEach((d, i) => {
        if (!d || !d.line) return
        const lineWithBrackets = d.line.startsWith('「') && d.line.endsWith('」')
          ? d.line
          : `「${d.line}」`
        content += `${i + 1}. ${d.situation || 'データなし'}${d.category ? ` [${d.category}]` : ''}\n`
        content += `   ${lineWithBrackets}\n`
        content += `   （${d.tone || 'データなし'}）\n`
      })
    } else {
      content += 'データなし\n'
    }
    content += '\n'

    // 作者向けメモ
    content += `【作者向けメモ】\n`
    content += result.authorNotes?.map(n => `  - ${n}`).join('\n') || '  - データなし'
    content += '\n\n'

    // 物語への組み込み
    content += `【物語への組み込み】\n`
    content += result.plotHooks?.map(h => `  - ${h}`).join('\n') || '  - データなし'
    content += '\n\n'

    // カスタムコンテキスト（ユーザー記述）
    if (result.customContext) {
      content += `【カスタムコンテキスト】\n`
      content += result.customContext
      content += '\n\n'
    }

    // 生成情報
    content += `生成日時: ${result.metadata?.generatedAt || new Date().toISOString()}\n`
    if (result.metadata?.seed) {
      content += `シード値: ${result.metadata.seed}\n`
    }

    // メモとタグ（保存データ固有の情報）
    if (character.memo) {
      content += `\nユーザーメモ: ${character.memo}\n`
    }

    if (character.tags && character.tags.length > 0) {
      content += `タグ: ${character.tags.join(', ')}\n`
    }

    content += '\n'
  })

  return content
}

// エクスポート用にキャラクターデータをクリーンアップ（不要なコンテキストを除去）
function cleanCharacterForExport(character: SavedCharacter): SavedCharacter {
  const cleanedInput = { ...character.input }
  delete cleanedInput.uploadedContent
  delete cleanedInput.images
  delete cleanedInput.relatedCharactersContext
  delete cleanedInput.communityContext
  delete cleanedInput.societyContext
  delete cleanedInput.itemsContext
  delete cleanedInput.skillsContext
  delete cleanedInput.worldContext
  return { ...character, input: cleanedInput }
}

// テキストファイルをダウンロード
function downloadTextFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// JSONファイルをダウンロード
function downloadJSONFile(data: any, filename: string) {
  const jsonString = JSON.stringify(data, null, 2)
  const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}